import { useEffect, useRef, useState } from "react";
import { Renderer } from "../../../engine/src/renderer.ts";
import type { Project } from "../../../engine/src/types.ts";
import type { TemplateInfo } from "../lib/api.ts";
import { Spinner, useApp } from "./common.tsx";
import { getLang, t } from "../lib/i18n.ts";

const FR: Record<string, [string, string]> = {
  "launch-neon": ["Lancement néon", "Lancement produit vertical : titre néon, avantages, appel à l'action."],
  "luxe-reveal": ["Révélation luxe", "Film de marque carré : révélation dorée élégante et logo."],
  "promo-pop": ["Promo pop", "Promo sociale verticale, couleurs vives et énergie."],
  "explainer-clean": ["Présentation claire", "Présentation horizontale : accroche, points clés, conclusion."],
  "phone-chat": ["Chat sur téléphone", "Promo d'app verticale : un téléphone 3D tourne, des bulles de chat apparaissent, avec le son."],
  "phone-wide": ["Vitrine téléphone", "Présentation horizontale : téléphone 3D, messages, appel à l'action."],
  "stat-pop": ["Gros chiffre", "Carré : un chiffre marquant pour frapper les esprits."],
  "title-3d": ["Titre 3D", "Ouverture verticale : anneau métallique, orbe et titre."],
  "product-launch": ["Lancement produit", "Horizontal : une phrase d'accroche puis un appel à l'action."],
  "app-showcase": ["Vitrine d'application", "Horizontal : un ordinateur présente votre produit, les fonctions éclatent, puis l'appel à l'action."],
  "tutorial-steps": ["Tutoriel en étapes", "Vertical : bande de progression, fenêtre d'appli et curseur qui clique à chaque étape."],
  "learn-api": ["Expliquer : API REST", "Cours horizontal : requête, serveur, base de données, réponse JSON, méthodes et codes HTTP."],
  "learn-neural": ["Expliquer : réseau de neurones", "Un vrai petit réseau entraîné en direct : poids, courbe d'erreur, table apprise."],
  "learn-sorting": ["Expliquer : tri à bulles", "L'algorithme s'exécute pas à pas avec son pseudo-code et son coût."],
  "learn-stack-queue": ["Expliquer : pile et file", "Empiler/dépiler et enfiler/défiler, simulés pas à pas."],
  "learn-water": ["Expliquer : molécule d'eau", "H₂O comparée à CO₂ : angle de liaison et polarité."],
  "orbit-logo": ["Logo en orbite", "Carré : un noyau et un anneau en orbite pour dévoiler un logo."],
};

/** Preview frames are drawn by the real engine, one at a time (a single GPU context at once), then kept as small JPEGs. */
const cache = new Map<string, string[]>();
let chain: Promise<unknown> = Promise.resolve();
const FRACTIONS = [0.2, 0.42, 0.64, 0.86];

export function posters(p: Project, fractions: number[] = FRACTIONS): string[] {
  const out: string[] = [], W = 360, scale = W / p.width;
  const small = document.createElement("canvas"); small.width = W; small.height = Math.round(p.height * scale);
  const big = document.createElement("canvas");
  const r = new Renderer(big, p.width, p.height);
  try {
    for (const f of fractions) {
      r.renderFrame(p, Math.round(p.duration_ms * f));
      const ctx = small.getContext("2d")!; ctx.drawImage(big, 0, 0, small.width, small.height);
      out.push(small.toDataURL("image/jpeg", 0.82));
    }
  } finally { r.dispose(); }
  return out;
}

export function useTemplatePosters(id: string, active: boolean) {
  const { api } = useApp();
  const [frames, setFrames] = useState((cache.get(id) ?? null) as string[] | null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!active || frames || failed) return;
    let live = true;
    chain = chain.then(async () => {
      if (!live) return;
      try {
        await document.fonts?.ready;
        const p = await api.templateProject(id);
        const f = posters(p); cache.set(id, f); if (live) setFrames(f);
      } catch { if (live) setFailed(true); }
    });
    return () => { live = false; };
  }, [active, id]);
  return { frames, failed };
}

export function TemplateCard({ x, onUse, busy }: { key?: string; x: TemplateInfo; onUse: (id: string) => void; busy: string | null }) {
  const ref = useRef(null as HTMLDivElement | null);
  const [seen, setSeen] = useState(false), [hot, setHot] = useState(false), [i, setI] = useState(0);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    if (typeof IntersectionObserver === "undefined") { setSeen(true); return; }
    const io = new IntersectionObserver((e) => { if (e.some((y) => y.isIntersecting)) { setSeen(true); io.disconnect(); } }, { rootMargin: "200px" });
    io.observe(el); return () => io.disconnect();
  }, []);
  const { frames, failed } = useTemplatePosters(x.id, seen);
  const fr = getLang() === "fr" ? FR[x.id] : undefined, name = fr ? fr[0] : x.name, desc = fr ? fr[1] : x.description;
  useEffect(() => {
    if (!hot || !frames) { setI(0); return; }
    const h = setInterval(() => setI((n: number) => (n + 1) % frames.length), 900);
    return () => clearInterval(h);
  }, [hot, frames]);
  const ratio = x.format === "vertical" ? "9 / 16" : x.format === "square" ? "1 / 1" : "16 / 9";
  return (
    <article className={"pin tpin " + x.format} ref={ref}>
      <div className="pin-img" style={{ aspectRatio: ratio, ...(busy !== null ? { cursor: "progress" } : {}) }} onPointerEnter={() => setHot(true)} onPointerLeave={() => setHot(false)} onFocus={() => setHot(true)} onBlur={() => setHot(false)}
        tabIndex={0} role="button" aria-label={`${t("templates.use")} ${name}`}
        onClick={() => { if (busy === null) onUse(x.id); }}
        onKeyDown={(e: any) => { if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); if (busy === null) onUse(x.id); } }}>
        {frames ? frames.map((src: string, k: number) => <img key={k} src={src} alt="" className={k === i ? "on" : ""} draggable={false} />)
          : failed ? <span className="pin-ph">{x.format}</span> : <span className="pin-ph"><Spinner /></span>}
        <span className="pin-tag">{x.format === "vertical" ? "9:16" : x.format === "square" ? "1:1" : "16:9"}</span>
        <div className="pin-over"><strong>{name}</strong><p>{desc}</p>
          <button className="btn primary sm" disabled={busy !== null} onClick={(e: any) => { e.stopPropagation(); onUse(x.id); }}>{busy === x.id && <Spinner />}{t("templates.use")}</button></div>
      </div>
      <div className="pin-meta"><strong>{name}</strong></div>
    </article>
  );
}

/** Poster frames of a project the user just got (the AI result), drawn by the engine; one GPU context at a time. */
export function useProjectPosters(p: Project | null): string[] | null {
  const [frames, setFrames] = useState(null as string[] | null);
  useEffect(() => {
    setFrames(null); if (!p) return;
    let live = true;
    chain = chain.then(async () => {
      if (!live) return;
      try { await document.fonts?.ready; const f = posters(p, [0.15, 0.35, 0.55, 0.75, 0.92]); if (live) setFrames(f); } catch { if (live) setFrames([]); }
    });
    return () => { live = false; };
  }, [p?.id, p?.revision]);
  return frames;
}

/** One still of a saved project, drawn by the engine and remembered per (id, revision) so the board is instant the next time. */
const memo = new Map<string, string>();
const KEY = (id: string, rev: number) => `modila.thumb.v1.${id}.${rev}`;
export function cachedThumb(id: string, rev: number): string | null {
  const k = KEY(id, rev); if (memo.has(k)) return memo.get(k)!;
  try { const v = localStorage.getItem(k); if (v) { memo.set(k, v); return v; } } catch { /* storage blocked */ }
  return null;
}
function remember(id: string, rev: number, url: string): void {
  const k = KEY(id, rev); memo.set(k, url);
  try {
    for (const key of Object.keys(localStorage)) if (key.startsWith(`modila.thumb.v1.${id}.`) && key !== k) localStorage.removeItem(key);      // older revisions of the same project
    const all = Object.keys(localStorage).filter((x) => x.startsWith("modila.thumb.v1."));
    if (all.length > 80) localStorage.removeItem(all[0]!);
    localStorage.setItem(k, url);
  } catch { /* quota or blocked: the in-memory copy is enough */ }
}
export function renderThumb(p: Project): Promise<string> {
  const run = chain.then(async () => {
    await document.fonts?.ready;
    const first = [...p.scenes].sort((a, b) => a.start_ms - b.start_ms)[0];
    const at = first ? Math.min(p.duration_ms, first.start_ms + first.duration_ms * 0.45) : p.duration_ms * 0.3;
    return posters(p, [at / Math.max(1, p.duration_ms)])[0]!;
  });
  chain = run.catch(() => undefined);
  return run.then((u) => { remember(p.id, p.revision, u); return u; });
}
